import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8" data-testid="forgot-password-card">
        {!sent ? (
          <form onSubmit={onSubmit} data-testid="forgot-password-form">
            <p className="label-overline">Account recovery</p>
            <h2 className="font-heading text-2xl font-semibold mt-2 text-slate-900">Forgot password?</h2>
            <p className="text-sm text-slate-500 mt-1">
              Enter your email and we'll send a one-time reset link. The link expires in 1 hour.
            </p>
            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email" className="label-overline">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2"
                  placeholder="john.doe@email.com"
                  data-testid="forgot-password-email"
                />
              </div>
              {error && (
                <div className="border border-red-200 bg-red-50 text-red-700 text-sm p-3 rounded-sm" data-testid="forgot-password-error">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="forgot-password-submit">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Send reset link
              </Button>
              <Link to="/login" className="block text-sm text-slate-500 hover:text-slate-700 text-center" data-testid="forgot-password-back-login">
                <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Back to sign in
              </Link>
            </div>
          </form>
        ) : (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="w-14 h-14 rounded-full bg-emerald-50 grid place-items-center mx-auto">
              <MailCheck className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold mt-4 text-slate-900">Check your email</h2>
            <p className="text-sm text-slate-500 mt-2">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link. It expires in 1 hour.
            </p>
            <Link to="/login" className="block mt-6">
              <Button variant="outline" className="w-full" data-testid="forgot-password-back-login-after">
                Back to sign in
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
